import React, { useEffect, useRef, useState } from "react";
import { BarChartComp, ButtonComp, TabsComp } from "../../../../components";
import RecentActivities from "./RecentActivities";
import DashboardSkeletonLoader from "../../../../components/Common/DashboardSkeletonLoader";
import SetupChecklist from "./SetupChecklist";
import SecurityReviewBanner from "./SecurityReviewBanner";
import { SkeletonBlock } from "../../../../components/Dashboard";
import StaffHomeView from "./StaffHomeView";
import {
  buildAttentionTiles,
  buildLoanTotals,
  buildMoneyTiles,
  buildPeopleRows,
  smsBalanceNaira,
  subscriptionStatus,
  toMeetingRow,
} from "./staffHomeModel";

import { useSelector } from "react-redux";
import { selectCurrentUserData } from "../../../../store/auth";
import { useDashboardQuery } from "../../../../store/admin/dashboard/dashboardSlice";
import { useGetCurrentStaffProfileQuery } from "../../../../store/admin/staff";
import {
  useCreateSmsTopupAfterPaymentMutation,
} from "../../../../store/admin/communicationSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { useSubscription } from "../../../../hooks/useSubscription";
import { staffCanViewDashboard } from "../../../../utils/staffRoleModules";
import toast from "react-hot-toast";
import { formatMoney } from "../../../../utils";
import { getEmptyMeetingsTitle } from "../../../../utils/meetings";
import SmsTopupModal from "./SmsTopupModal";

export default function HomeComp() {
  const userData = useSelector(selectCurrentUserData);
  const firstName = userData?.name?.split(" ")[0] || "User";
  const { data: staffProfile } = useGetCurrentStaffProfileQuery();
  const currentBranch = (staffProfile || userData)?.current_branch;
  const location = useLocation();
  const navigate = useNavigate();
  const { isActive } = useSubscription();
  const canViewDashboard = staffCanViewDashboard(userData?.role);

  const searchParams = new URLSearchParams(location.search);

  const [selectedPeriod, setSelectedPeriod] = useState(
    searchParams.get("mode") ?? "Weekly"
  );
  const [selectedYear, setSelectedYear] = useState(
    searchParams.get("year") ?? new Date().getFullYear().toString()
  );
  const [selectedMeetingFilter, setSelectedMeetingFilter] = useState("Today");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showSmsTopupModal, setShowSmsTopupModal] = useState(false);
  
  // Skip dashboard query when subscription is inactive or role cannot view it
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch: refetchDashboard,
  } = useDashboardQuery(
    {
      weekly: selectedPeriod === "Weekly",
      year: selectedYear,
    },
    {
      skip: !isActive || !canViewDashboard,
      // Always refresh when returning to dashboard after payments/actions elsewhere
      refetchOnMountOrArgChange: true,
    }
  );
  const dashboardData = data?.data || null;
  const showInitialSkeleton =
    isActive &&
    canViewDashboard &&
    !dashboardData &&
    !isError &&
    (isLoading || isFetching);

  const [recordSmsTopup, { isLoading: recordSmsTopupLoading }] =
    useCreateSmsTopupAfterPaymentMutation();
  const smsTopupCallbackHandled = useRef(false);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("sms_topup") !== "1") {
      return;
    }
    const ref = p.get("reference") || p.get("trxref");
    if (!ref || smsTopupCallbackHandled.current) {
      return;
    }
    smsTopupCallbackHandled.current = true;
    recordSmsTopup({ paystack_transaction_reference: ref })
      .unwrap()
      .then((res) => {
        const credited =
          res?.amount_paid != null
            ? formatMoney(Number(res.amount_paid).toFixed(2), true)
            : null;
        toast.success(
          credited
            ? `Payment successful. ${credited} credited to your SMS balance.`
            : "Payment successful. Your SMS balance has been credited."
        );
        refetchDashboard();
      })
      .catch((err) => {
        smsTopupCallbackHandled.current = false;
        toast.error(
          err?.data?.message ||
            err?.message ||
            "Could not credit SMS top-up. Contact support if payment left your account."
        );
      })
      .finally(() => {
        navigate("/super_admin/dashboard", { replace: true });
      });
  }, [location.search, navigate, recordSmsTopup, refetchDashboard]);

  // Update current date every day at midnight
  useEffect(() => {
    const updateDate = () => {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      setCurrentDate(today);
    };

    // Update immediately
    updateDate();

    // Set up interval to update at midnight
    const now = new Date();
    const tomorrow = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1
    );
    const timeUntilMidnight = tomorrow.getTime() - now.getTime();

    const midnightTimer = setTimeout(() => {
      updateDate();
      // Then update every 24 hours
      setInterval(updateDate, 24 * 60 * 60 * 1000);
    }, timeUntilMidnight);

    return () => clearTimeout(midnightTimer);
  }, []);

  // Generate meeting filter options for the next 7 days
  const generateMeetingFilterOptions = () => {
    const options = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() + i);

      const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
      const monthDay = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });

      let label;
      if (i === 0) {
        label = "Today";
      } else if (i === 1) {
        label = "Tomorrow";
      } else {
        label = `${dayName}, ${monthDay}`;
      }

      options.push({
        name: <span className="h50">{label}</span>,
        action: () => {
          setSelectedMeetingFilter(label);
        },
        value: i, // days from today
      });
    }

    return options;
  };

  // Local YYYY-MM-DD (avoid UTC shift from toISOString on date-only strings)
  const toLocalDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const parseEventDateString = (value) => {
    if (!value) return null;
    const raw = String(value).slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null;
  };

  // Filter meetings based on selected date (event overlaps that calendar day)
  const getFilteredMeetings = () => {
    if (!dashboardData?.upcomingMeetings) return [];

    const selectedDayIndex = generateMeetingFilterOptions().findIndex(
      (option) => option.name.props.children === selectedMeetingFilter
    );

    if (selectedDayIndex === -1) return dashboardData.upcomingMeetings;

    const selectedDate = new Date(currentDate);
    selectedDate.setDate(currentDate.getDate() + selectedDayIndex);
    const selectedDateString = toLocalDateString(selectedDate);

    return dashboardData.upcomingMeetings.filter((meeting) => {
      const start = parseEventDateString(meeting.start_date);
      const end =
        parseEventDateString(meeting.end_date) || start;
      if (!start) return false;
      return start <= selectedDateString && end >= selectedDateString;
    });
  };

  const emptyMeetingsTitle = getEmptyMeetingsTitle(selectedMeetingFilter);

  useEffect(() => {
    searchParams.set("mode", selectedPeriod);
    searchParams.set("year", selectedYear);
    // search.set('upcoming', "today")
    navigate({
      hash: location.hash,
      pathname: location.pathname,
      search: searchParams.toString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, selectedYear]);

  const useTab = [
    {
      name: "Loans",
      component: (
        <div className="mb-5">
          {/* <TableCompData
            columns={activitiesColumns}
            data={dashboardData?.loans || []}
            pagination={true}
            customStyles={tableCustomStyles}
            message={
              <div className="text-center py-4 text-muted">
                No loan activities found.
              </div>
            }
          /> */}
          <RecentActivities
            data={dashboardData?.loans || []}
            type="loan"
            emptyMessage="No loan activities found."
          />
        </div>
      ),
    },
    {
      name: (
        <span>
          <span className="d-block d-md-none">Contr.</span>
          <span className="d-none d-md-block">Contributions</span>
        </span>
      ),
      component: (
        <div className="mb-5">
          {/* <TableCompData
            columns={activitiesColumns}
            data={dashboardData?.contributions || []}
            pagination={true}
            customStyles={tableCustomStyles}
            message={
              <div className="text-center py-4 text-muted">
                No contribution activities found.
              </div>
            }
          /> */}
          <RecentActivities
            data={dashboardData?.contributions || []}
            type="contribution"
            emptyMessage="No contribution activities found."
          />
        </div>
      ),
    },
    // {
    //   name: (
    //     <span>
    //       <span className="d-block d-md-none">Recent</span>
    //       <span className="d-none d-md-block">Recent Transactions</span>
    //     </span>
    //   ),
    //   component: (
    //     <div className="mb-5 px-3">
    //       <TableCompData
    //         columns={activitiesColumns}
    //         data={dashboardData?.recentTransactions || []}
    //         pagination={true}
    //         customStyles={tableCustomStyles}
    //         message={
    //           <div className="text-center py-4 text-muted">
    //             No recent transactions found.
    //           </div>
    //         }
    //       />
    //     </div>
    //   ),
    // },
  ];

  return (
    <main className="mx-2 mx-lg-4 animate__animated animate__fadeIn mb-5">
      {!canViewDashboard ? (
        <div className="text-center py-5 mt-5">
          <h5 className="fw-bold text-black1 mb-2">Dashboard unavailable</h5>
          <p className="text-muted mb-0">
            Your role does not include dashboard access.
          </p>
        </div>
      ) : !isActive ? (
        <div className="text-center py-5 mt-5">
          <h5 className="fw-bold text-black1 mb-2">Subscription inactive</h5>
          <p className="text-muted mb-0">
            Activate your cooperative subscription to view the dashboard.
          </p>
        </div>
      ) : showInitialSkeleton ? (
        <DashboardSkeletonLoader />
      ) : dashboardData ? (
        <StaffHomeView
          firstName={firstName}
          branchName={currentBranch?.name}
          banner={<SecurityReviewBanner />}
          attentionTiles={buildAttentionTiles(dashboardData.attention)}
          money={buildMoneyTiles(dashboardData)}
          loans={buildLoanTotals(dashboardData)}
          people={buildPeopleRows(dashboardData)}
          period={selectedPeriod}
          onPeriodChange={setSelectedPeriod}
          year={selectedYear}
          onYearChange={setSelectedYear}
          yearOptions={Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() - i).toString())}
          chart={
            !isFetching ? (
              <BarChartComp
                chartData={(dashboardData.statistics || []).map((stat) => ({
                  name: selectedPeriod === "Weekly" ? stat.week : stat.month,
                  ...stat,
                }))}
              />
            ) : (
              <SkeletonBlock height={300} />
            )
          }
          activity={
            <TabsComp
              lineHeight={"2px"}
              TabArray={useTab}
              borderWidth="3px"
              lineBottom={"24px"}
              input="name"
            />
          }
          meetings={{
            days: generateMeetingFilterOptions().map((option) => option.name.props.children),
            selected: selectedMeetingFilter,
            onSelect: setSelectedMeetingFilter,
            items: getFilteredMeetings().map(toMeetingRow),
            emptyTitle: emptyMeetingsTitle,
          }}
          sms={{
            amount: smsBalanceNaira(dashboardData),
            onTopUp: () => setShowSmsTopupModal(true),
            busy: recordSmsTopupLoading,
          }}
          subscription={subscriptionStatus(dashboardData.subscription)}
          checklist={<SetupChecklist />}
        />
      ) : (
        <div className="text-center py-5 mt-5 px-3">
          <h5 className="fw-bold text-black1 mb-2">
            {isError ? "Couldn’t load dashboard" : "No dashboard data"}
          </h5>
          <p className="text-muted mb-4">
            {error?.data?.message ||
              error?.message ||
              "Something went wrong while loading your overview. Try again."}
          </p>
          <ButtonComp
            containerClassName="w-auto d-inline-block"
            btnText="Retry"
            btnClassName="border-0 text-white py-2 px-4"
            ButtonInlineStyle={{
              backgroundColor: "var(--color-primary)",
              minWidth: 120,
              borderRadius: 10,
            }}
            onClick={() => refetchDashboard()}
          />
        </div>
      )}
      <SmsTopupModal
        show={showSmsTopupModal}
        onClose={() => setShowSmsTopupModal(false)}
      />
    </main>
  );
}
